import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ItemVariant, POSItem } from '@/types';
import { posApi } from '@/services/api';

export interface BulkSelectableRow {
  key: string;
  item: POSItem;
  variant?: ItemVariant;
  label: string;
  categoryId?: string;
  categoryName?: string;
  salePrice: number;
  wholesalePrice: number;
  resellerPrice: number;
  minimumPrice: number;
  deliveryPrice: number;
  costPrice: number;
}

export function useBulkItemSelection(defaultItemType: 'Product' | 'Service' | '' = 'Product') {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [itemType, setItemType] = useState<'Product' | 'Service' | ''>(defaultItemType);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [loadedVariants, setLoadedVariants] = useState<Record<string, ItemVariant[]>>({});
  const [loadingVariants, setLoadingVariants] = useState<Record<string, boolean>>({});

  const { data: categories = [] } = useQuery<{ name: string; category_name: string; category_type: string }[]>({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await posApi.getCategories();
      return res.data.message;
    },
  });

  const productCategories = useMemo(
    () => categories.filter((c) => c.category_type === 'Product'),
    [categories],
  );

  const { data: searchResults = [], isLoading } = useQuery({
    queryKey: ['bulkItemSelection', search, category, itemType],
    queryFn: async () => {
      const res = await posApi.getItems({
        search,
        category: category === 'All' ? undefined : category,
        item_type: itemType || undefined,
        limit: 200,
      });
      return (res.data.message.items as POSItem[]) || [];
    },
  });

  const categoryNameById = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((c) => {
      map[c.name] = c.category_name;
    });
    return map;
  }, [categories]);

  const flatRows = useMemo((): BulkSelectableRow[] => {
    const rows: BulkSelectableRow[] = [];
    for (const item of searchResults) {
      const variants = loadedVariants[item.name];
      if (item.has_variants && variants?.length) {
        for (const v of variants) {
          rows.push({
            key: `${item.name}__${v.name}`,
            item,
            variant: v,
            label: `${item.item_name} — ${v.variant_name}`,
            categoryId: item.category,
            categoryName: categoryNameById[item.category] || item.category,
            salePrice: Number(v.price) || 0,
            wholesalePrice: Number(v.wholesale_price) || 0,
            resellerPrice: Number(v.reseller_price) || 0,
            minimumPrice: Number(v.minimum_price) || 0,
            deliveryPrice: Number(v.delivery_price) || 0,
            costPrice: Number(v.cost_price) || 0,
          });
        }
      } else if (!item.has_variants) {
        rows.push({
          key: item.name,
          item,
          label: item.item_name,
          categoryId: item.category,
          categoryName: categoryNameById[item.category] || item.category,
          salePrice: Number(item.sale_price) || 0,
          wholesalePrice: Number(item.wholesale_price) || 0,
          resellerPrice: Number(item.reseller_price) || 0,
          minimumPrice: Number(item.minimum_price) || 0,
          deliveryPrice: Number(item.delivery_price) || 0,
          costPrice: Number(item.cost_price) || 0,
        });
      }
    }
    return rows;
  }, [searchResults, loadedVariants, categoryNameById]);

  const toggleExpand = useCallback(async (item: POSItem) => {
    const isExpanded = !!expandedItems[item.name];
    setExpandedItems((prev) => ({ ...prev, [item.name]: !isExpanded }));
    if (!isExpanded && !loadedVariants[item.name] && !loadingVariants[item.name]) {
      setLoadingVariants((prev) => ({ ...prev, [item.name]: true }));
      try {
        const res = await posApi.getItemVariants(item.name);
        setLoadedVariants((prev) => ({ ...prev, [item.name]: (res.data.message as ItemVariant[]) || [] }));
      } finally {
        setLoadingVariants((prev) => ({ ...prev, [item.name]: false }));
      }
    }
  }, [expandedItems, loadedVariants, loadingVariants]);

  const toggleKey = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const getAllVisibleKeys = useCallback(async (): Promise<string[]> => {
    const keys: string[] = [];
    for (const item of searchResults) {
      if (item.has_variants) {
        let variants = loadedVariants[item.name];
        if (!variants) {
          const res = await posApi.getItemVariants(item.name);
          variants = (res.data.message as ItemVariant[]) || [];
          setLoadedVariants((prev) => ({ ...prev, [item.name]: variants! }));
          setExpandedItems((prev) => ({ ...prev, [item.name]: true }));
        }
        for (const v of variants) {
          keys.push(`${item.name}__${v.name}`);
        }
      } else {
        keys.push(item.name);
      }
    }
    return keys;
  }, [searchResults, loadedVariants]);

  const selectAllVisible = useCallback(async () => {
    const keys = await getAllVisibleKeys();
    setSelectedKeys(new Set(keys));
  }, [getAllVisibleKeys]);

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  const selectedRows = useMemo(
    () => flatRows.filter((r) => selectedKeys.has(r.key)),
    [flatRows, selectedKeys],
  );

  return {
    search,
    setSearch,
    category,
    setCategory,
    itemType,
    setItemType,
    categories: productCategories,
    searchResults,
    flatRows,
    selectedKeys,
    selectedRows,
    isLoading,
    expandedItems,
    loadingVariants,
    loadedVariants,
    toggleExpand,
    toggleKey,
    selectAllVisible,
    clearSelection,
    getAllVisibleKeys,
    setSelectedKeys,
  };
}
